import {createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {FieldType, SqlOperation, TableI} from "../Types";
import {RootState} from "../store/store";

export type ManageSchemaModes = (typeof MANAGE_SCHEMA_MODES)[number];

export const MANAGE_SCHEMA_MODES = [
    "view", "edit", "add"
] as const

export interface ManageSchemaFrontTableI {
    fields: {
        mode: ManageSchemaModes,
    },
    filters: {
        mode: ManageSchemaModes,
    },
    main: {
        mode: ManageSchemaModes,
    }
    data: TableI
}

type SectionView = "fields" | "filters" | "main";

export type ShowFieldsWeightU = (typeof SHOW_FIELDS_WEIGHT)[number];
export type FrontFieldsViewRenderTypeU = (typeof FRONT_FIELDS_VIEW_RENDER_TYPE)[number];


type SelectedFacet = "front" | "back";

type BackTableMode = "view" | "edit";

interface SchemaManageSliceI {
    active: boolean,
    archiveOpen: boolean,
    selectedFacet: SelectedFacet
    front: {
        showFieldsWeight: ShowFieldsWeightU,
        showInlineOnly: boolean,
        showDetailedOnly: boolean,
        showEditableOnly: boolean,
        showNullableOnly: boolean,
        showUndefined: boolean
        selectedTable: string,
        renderType: FrontFieldsViewRenderTypeU
        sectionView: SectionView
        tables: { [key: string]: ManageSchemaFrontTableI }
    },
    back: {
        selectedTable: string | null,
        proj: BackProj | null,
        mode: ManageSchemaModes,
        staticTables: {
            [key: string]: BackTable | null
        }
        etherealTables: {
            [key: string]: BackTable | null
        }
        tables: {
            [key: string]: BackTable
        },
        sqlSelectQueries: {
            [key: string]: string
        },
    }

}

export const SHOW_FIELDS_WEIGHT = [
    "all", "main"
] as const

export const FRONT_FIELDS_VIEW_RENDER_TYPE = [
    "list", "tiles", "lightMode"
] as const

type FrontState = SchemaManageSliceI['front'];
type BackState = SchemaManageSliceI['back'];


export const schemaManageSlice = createSlice({
    name: 'schemaManage',
    initialState: {
        active: false,
        archiveOpen: false,
        selectedFacet: "back",
        front: {
            showFieldsWeight: "all",
            showInlineOnly: false,
            showDetailedOnly: false,
            showEditableOnly: false,
            showNullableOnly: false,
            sectionView: "fields",
            showUndefined: false,
            renderType: "tiles",
            tables: {},
        },
        back: {
            selectedTable: null,
            proj: null,
            mode: "edit",
            tables: {},
            staticTables: {},
            etherealTables: {},
            sqlSelectQueries: {}
        }
    } as SchemaManageSliceI,
    reducers: {
        setSchemaManagePageActive: (state, action: PayloadAction<boolean>) => {
            state.active = action.payload
        },
        setSchemaManagePageArchiveOpen: (state, action: PayloadAction<boolean>) => {
            state.archiveOpen = action.payload
        },
        setSchemaManageSelectedFacet: (state, action: PayloadAction<SelectedFacet>) => {
            state.selectedFacet = action.payload
        },
        setFrontField: (state, action: PayloadAction<{ key: keyof FrontState, value: any }>) => {
            (state.front as any)[action.payload.key] = action.payload.value;
        },
        setBackField: (state, action: PayloadAction<{ key: keyof BackState, value: any }>) => {
            (state.back as any)[action.payload.key] = action.payload.value;
        },
        setBackTableEthereal: (state, action: PayloadAction<{ key: string, table: BackTable }>) => {
            const {table, key} = action.payload;
            if (state.back.tables[key]) {
                alert("Таблиця з таким ключем вже існує!")
                return
            }
            state.back.tables[key] = table
            state.back.etherealTables[key] = table
            state.back.staticTables[key] = null
            state.back.sqlSelectQueries[key] = 'UNAVAILABLE FOR NOW'
            state.back.mode = 'edit'
            state.back.proj!.tableNames = [...state.back.proj!.tableNames, key]
            state.back.selectedTable = key
        },
        setBackTableJoin: (
            state,
            action: PayloadAction<{
                table: string;
                joinKey: string;
                value: string | undefined;
            }>
        ) => {
            const {table, joinKey, value} = action.payload;

            //delete case
            if (value === undefined) {
                delete state.back.etherealTables[table]?.joins?.[joinKey];
                if (state.back.staticTables[table]?.joins?.[joinKey])
                    state.back.tables[table].joins[joinKey] = state.back.staticTables[table]?.joins?.[joinKey]!!
                else
                    delete state.back.tables[table]?.joins?.[joinKey];
            } else {
                state.back.etherealTables[table]!!.joins[joinKey] = value;
                state.back.tables[table].joins[joinKey] = value;
            }
        },
        setBackTableField: (
            state,
            action: PayloadAction<{
                table: string;
                fieldKey: string;
                value: BackTableField | undefined;
            }>
        ) => {
            const {table, fieldKey, value} = action.payload;

            //delete case
            if (value === undefined) {
                delete state.back.etherealTables[table]?.fieldsList?.[fieldKey];
                if (state.back.staticTables[table]?.fieldsList?.[fieldKey])
                    state.back.tables[table].fieldsList[fieldKey] = state.back.staticTables[table]?.fieldsList?.[fieldKey]!!
                else
                    delete state.back.tables[table]?.fieldsList?.[fieldKey];
            } else {
                state.back.etherealTables[table]!!.fieldsList[fieldKey] = value;
                state.back.tables[table].fieldsList[fieldKey] = value;
            }
        },
        setBackTableFilter: (
            state,
            action: PayloadAction<{
                table: string;
                filterKey: string;
                value: BackTableFilter | undefined;
            }>
        ) => {
            const {table, filterKey, value} = action.payload;

            //delete case
            if (value === undefined) {
                delete state.back.etherealTables[table]?.filtersList?.[filterKey];
                if (state.back.staticTables[table]?.filtersList?.[filterKey])
                    state.back.tables[table].filtersList[filterKey] = state.back.staticTables[table]?.filtersList?.[filterKey]!!
                else
                    delete state.back.tables[table]?.filtersList?.[filterKey];
            } else {
                state.back.etherealTables[table]!!.filtersList[filterKey] = value;
                state.back.tables[table].filtersList[filterKey] = value;
            }
        },
        setBackTableUpdateField: (
            state,
            action: PayloadAction<{
                table: string;
                fieldKey: string;
                value: PhpType | undefined;
            }>
        ) => {
            const {table, fieldKey, value} = action.payload;

            //delete case
            if (value === undefined) {
                delete state.back.etherealTables[table]?.updatableFields?.[fieldKey];
                if (state.back.staticTables[table]?.updatableFields?.[fieldKey])
                    state.back.tables[table].updatableFields[fieldKey] = state.back.staticTables[table]?.updatableFields?.[fieldKey]!!
                else
                    delete state.back.tables[table]?.updatableFields?.[fieldKey];
            } else {
                state.back.etherealTables[table]!!.updatableFields[fieldKey] = value;
                state.back.tables[table].updatableFields[fieldKey] = value;
            }
        },
        setBackTableNullableFields: (
            state,
            action: PayloadAction<{
                table: string;
                value: string[];
            }>
        ) => {
            const {table, value} = action.payload;

            state.back.etherealTables[table]!!.nullableFields = value;
            if (state.back.staticTables[table] !== null) {
                state.back.tables[table].nullableFields = Array.from(new Set(state.back.staticTables[table]!.nullableFields.concat(value)))
            } else
                state.back.tables[table].nullableFields = value;
        },
        setBackTableOuterField: (
            state,
            action: PayloadAction<{
                table: string;
                fieldKey: string;
                value: BackOuterField | undefined;
            }>
        ) => {
            const {table, fieldKey, value} = action.payload;

            //delete case
            if (value === undefined) {
                delete state.back.etherealTables[table]?.outerFields?.[fieldKey];
                if (state.back.staticTables[table]?.outerFields?.[fieldKey])
                    state.back.tables[table].outerFields[fieldKey] = state.back.staticTables[table]?.outerFields?.[fieldKey]!!
                else
                    delete state.back.tables[table]?.outerFields?.[fieldKey];
            } else {
                state.back.etherealTables[table]!!.outerFields[fieldKey] = value;
                state.back.tables[table].outerFields[fieldKey] = value;
            }
        },
        setBackTablePermaField: (
            state,
            action: PayloadAction<{
                table: string;
                fieldKey: string;
                value: BackPermaFilter | undefined;
            }>
        ) => {
            const {table, fieldKey, value} = action.payload;

            //delete case
            if (value === undefined) {
                delete state.back.etherealTables[table]?.permaFilter?.[fieldKey];
                if (state.back.staticTables[table]?.permaFilter?.[fieldKey])
                    state.back.tables[table].permaFilter[fieldKey] = state.back.staticTables[table]?.permaFilter?.[fieldKey]!!
                else
                    delete state.back.tables[table]?.permaFilter?.[fieldKey];
            } else {
                state.back.etherealTables[table]!!.permaFilter[fieldKey] = value;
                state.back.tables[table].permaFilter[fieldKey] = value;
            }
        },
        setFrontSchemaTablesMode: (state, action: PayloadAction<{
            table: string,
            section: SectionView,
            mode: ManageSchemaModes
        }>) => {
            const {table, section, mode} = action.payload;
            state.front.tables[table][section].mode = mode
        },
    },
})

export const {
    setSchemaManagePageActive,
    setSchemaManagePageArchiveOpen,
    setSchemaManageSelectedFacet,
    setFrontSchemaTablesMode,
    setBackTableJoin,
    setBackTableField,
    setBackTableFilter,
    setBackTableUpdateField,
    setBackTableOuterField,
    setBackTablePermaField,
    setBackTableNullableFields,
    setBackTableEthereal
} = schemaManageSlice.actions

export const setFrontSchemaStateField = <K extends keyof FrontState>(
    key: K,
    value: FrontState[K]
) => schemaManageSlice.actions.setFrontField({key, value});

export const setBackSchemaStateField = <K extends keyof BackState>(
    key: K,
    value: BackState[K]
) => schemaManageSlice.actions.setBackField({key, value});

export const selectFrontSchemaState = (state: RootState) => state.schemaManage.front;

export const selectFilteredFields = createSelector(
    [
        selectFrontSchemaState,
        (state: RootState) => state.schemaManage.front.selectedTable,
    ],
    (front, selectedTable) => {

        console.log("selecting")
        const table = front.tables[selectedTable];
        if (!table) return [];

        return table.data.fields
            .map((f, i) => ({...f, index: i}))
            .filter(f => {
                if (front.showInlineOnly && !f.inline) return false;
                if (front.showDetailedOnly && !f.detailed) return false;
                if (front.showEditableOnly && !f.editable) return false;
                if (front.showNullableOnly && !f.nullable) return false;
                return true;
            });
    }
);

export default schemaManageSlice.reducer

export type BackProjType = "ethereal" | "mixed" | "static";

export interface BackProj {
    name: string,
    type: BackProjType,
    queries: Record<string, any>,
    tableNames: string[]
}

export type BackOuterField = {
    sql: string,
    depends: string[]
};

export type BackPermaFilter = {
    sql: string,
    depends: string[]
};

export interface BackTable {
    name: string;
    schema: string | null;
    forAdmin: boolean;
    pk: string;
    type: BackProjType;

    fieldsList: Record<string, BackTableField>;
    filtersList: Record<string, BackTableFilter>;

    joins: Record<string, string>;
    updatableFields: Record<string, PhpType>;
    nullableFields: string[];

    outerFields: Record<string, BackOuterField>;
    permaFilter: Record<string, BackPermaFilter>;
}

export type PhpType = "int" | "float" | "string" | "bool" | 'timestamp' | "date"

export interface BackTableField {
    // name: string;
    sqlQuery?: string | null;
    joinDependencies?: string[]; // уточни тип, якщо є структура
}

export const PHP_TYPE_MAP: Record<PhpType, { color: string; desc: string }> = {
    int: {
        desc: "int",
        color: "#007bff"
    },
    string: {
        desc: "string",
        color: "#a772ff"
    },
    float: {
        desc: "float",
        color: "#36d851"
    },
    bool: {
        desc: "bool",
        color: "#fd7e14"
    },
    timestamp: {
        desc: "timestamp",
        color: "#17a2b8"
    },
    date: {
        desc: "date",
        color: "#28a745"
    },
}

export const phpTypeToFieldType = (phpType: PhpType): FieldType => {
    switch (phpType) {
        case "int":
        case "float":
            return "number"
        case "bool":
            return "boolean"
        case "timestamp":
            return "datetime"
        case "date":
            return "date"
        default:
            return "string"
    }
};

export interface BackTableFilter {
    // filterField: BackTableField['name'];
    type: PhpType;
    appendAlias?: boolean;
    outer?: boolean;

    operationsList: SqlOperation[]; // імпортуй або визнач нижче
}

